import type {
  ApiError,
  SdkTwoFactorChallengeHandle,
  SdkUseTwoFactorOptions
} from '@Pages/pageRuntime/sdk/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  isRestartError,
  navigateAfterSignIn,
  postTwoFactor,
  type TwoFactorCompletion,
  twoFactorFlowUrl,
  useCancelPendingSignIn,
  useChallengeQuery,
  useTwoFactorCompletion,
  useTwoFactorTarget
} from './twoFactorFlows';

type Factor = { code: string } | { recovery_code: string };

export function useTwoFactorChallenge(
  options?: SdkUseTwoFactorOptions
): SdkTwoFactorChallengeHandle {
  const target = useTwoFactorTarget(options?.resourceId);
  const resourceId = target.resourceId;
  const isActive =
    target.pending !== null &&
    target.pending.stage === 'challenge' &&
    target.pending.resourceId === resourceId;
  const query = useChallengeQuery(resourceId, isActive);
  const cancel = useCancelPendingSignIn(resourceId);
  const queryClient = useQueryClient();
  const completion = useTwoFactorCompletion(resourceId);
  const needsBackupCodes = completion?.next === 'backup_codes';

  const verify = useMutation<TwoFactorCompletion, ApiError, Factor>({
    mutationFn: (factor) => {
      if (resourceId === null) {
        throw new Error('A two-factor challenge needs a resource.');
      }

      return postTwoFactor(twoFactorFlowUrl('challenge', resourceId), factor);
    },
    onSuccess: (completion) => {
      if (completion.next === 'backup_codes') {
        queryClient.setQueryData(['two-factor', 'completion', resourceId], completion);

        return;
      }

      navigateAfterSignIn(completion.url);
    }
  });

  const run = (factor: Factor) =>
    verify.mutateAsync(factor).then(
      () => true,
      () => false
    );

  const props = query.data;
  const actionError = verify.error ?? cancel.error;

  return {
    isActive,
    data: props && {
      step: props.smsConsentRequired ? 'consent' : 'code',
      maskedPhone: props.maskedPhone,
      resendAvailableIn: props.resendAvailableIn,
      status: props.status ?? null,
      deliveryError: props.deliveryError ?? null
    },
    isLoading: isActive && query.isLoading,
    error: query.error,
    isPending: verify.isPending || cancel.isPending,
    actionError,
    restartRequired: isRestartError(query.error) || isRestartError(actionError),
    needsBackupCodes,
    verifyCode: (code) => run({ code }),
    verifyBackupCode: (code) => run({ recovery_code: code.trim() }),
    cancel: () =>
      cancel.mutateAsync().then(
        () => undefined,
        () => undefined
      )
  };
}
